import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { CompanyStatus } from '@/schema'
import { SortOrder } from '@/types/common'
import { convertSortToApiFormat } from '@/utils'
import {
  DEFAULT_PAGE,
  MIN_SEARCH_LENGTH,
  SortDirection,
  TABLE_PAGE_SIZE,
} from '@/utils/constants'

import { companyApi } from './services'
import { CreateCompanyRequest, UpdateCompanyRequest } from './types'

const QUERY_KEY_COMPANY_LIST = 'company-list'

const COMPANY_LIST_QUERY_KEY = (params: (string | number)[]) =>
  [QUERY_KEY_COMPANY_LIST, ...(params ? [params] : [])] as const

const QUERY_KEY_COMPANY_DETAIL = 'company-detail'

const COMPANY_DETAIL_QUERY_KEY = (id?: string) => [QUERY_KEY_COMPANY_DETAIL, id]

const COMPANY_DETAIL_STALE_TIME = 5 * 60 * 1000

export const useCompanyList = (params: {
  search?: string
  page?: number
  rowsPerPage?: number
  sort?: {
    column: string
    direction: SortOrder
  }
  status?: CompanyStatus
  enabled?: boolean
}) => {
  const {
    search = '',
    page = DEFAULT_PAGE,
    rowsPerPage = TABLE_PAGE_SIZE,
    sort,
    status,
    enabled = true,
  } = params
  const query = search.trim().length >= MIN_SEARCH_LENGTH ? search.trim() : ''
  const sortBy = sort ? convertSortToApiFormat(sort) : ''

  // Build filter_by object
  const filterBy: { [key: string]: unknown[] } = {}
  if (status) {
    filterBy.status = [status]
  }

  return useQuery({
    queryKey: COMPANY_LIST_QUERY_KEY([
      query,
      page,
      rowsPerPage,
      sortBy,
      status || '',
    ]),
    queryFn: async () => {
      const { data, pagination } = await companyApi.getCompanyList({
        q: query,
        offset: page * rowsPerPage,
        limit: rowsPerPage,
        sort_by: sortBy,
        filter_by: Object.keys(filterBy).length > 0 ? filterBy : undefined,
      })

      return { data, pagination }
    },
    staleTime: Infinity,
    enabled,
  })
}

export const useCompanyById = (id: string) => {
  return useQuery({
    queryKey: COMPANY_DETAIL_QUERY_KEY(id),
    queryFn: async () => {
      const response = await companyApi.getCompanyById(id)
      return response.data
    },
    enabled: !!id,
    staleTime: COMPANY_DETAIL_STALE_TIME,
  })
}

export const useCreateCompany = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateCompanyRequest) => companyApi.createCompany(data),
    onSuccess: () => {
      // Invalidate and refetch company list
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_COMPANY_LIST],
      })
    },
  })
}

export const useUpdateCompany = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCompanyRequest }) =>
      companyApi.updateCompany(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_COMPANY_LIST],
      })
      queryClient.invalidateQueries({
        queryKey: COMPANY_DETAIL_QUERY_KEY(String(variables.id)),
      })
    },
  })
}

export const useDeleteCompany = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => companyApi.deleteCompany(id),
    onSuccess: () => {
      // Invalidate and refetch company list - use refetchType: 'active' to preserve current query parameters
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_COMPANY_LIST],
        refetchType: 'active',
      })
    },
  })
}

export const useCompanyInfinite = () =>
  useInfiniteQuery({
    queryKey: COMPANY_LIST_QUERY_KEY([]),
    queryFn: ({ pageParam = DEFAULT_PAGE }) =>
      companyApi.getCompanyList({
        sort_by: convertSortToApiFormat({
          column: 'name',
          direction: SortDirection.ASC,
        }),
        limit: TABLE_PAGE_SIZE,
        offset: pageParam * TABLE_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const hasMore = lastPage?.pagination?.has_next ?? false
      return hasMore ? allPages.length : undefined
    },
    initialPageParam: DEFAULT_PAGE,
    staleTime: Infinity,
  })
